import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/styles";
import { FormControlLabel, Checkbox, Snackbar, Divider } from "@material-ui/core";
import { ReflexContainer, ReflexSplitter, ReflexElement } from "react-reflex";
import { debounce } from "lodash";
import { Alert } from "@material-ui/lab";
import uiConf from "../../config/uiConf";
import { useRecoilValue } from "recoil";

import authProfileState from "../../states/authProfileState";

import apiClient from "../../lib/common/apiClient";
import checkValidation from "../../lib/utils/checkValidation";
import checkConditionWithMsg from "../../lib/utils/checkConditionWithMsg";

import Dialog from "../dialog/functional/Dialog";
import DialogContent from "../dialog/functional/DialogContent";
import DialogTitle from "../dialog/functional/DialogTitle";
import DialogActionsOkCancel from "../dialog/functional/DialogActionsOkCancel";
import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import AutoForm from "../form/functional/AutoForm";
import "../grid/layout/DataGridMomSysOrderPurchase.css";
import layoutConf from "../../config/layoutConf";
import defineConf from "../../config/defineConf";
import DialogHeaderWithClose from "../dialog/functional/DialogHeaderWithClose";
import failureKey from "../../lib/common/failureKey";

const useStyles = makeStyles((theme) => ({
    keepOpenSwitch: {
        marginLeft: "8px",
    },

    label: {
        fontSize: "smaller",
        color: theme.palette.primary.text,
    },
}));

function EguInputInspectionRegisterDialog(props) {
    const {
        id,
        schema,

        moveable,
        resizable,
        fullWidth,
        maxWidth,

        onClose,
        onInitialized,
        onSuccess,

        ...others
    } = props;

    const classes = useStyles();
    const { t } = useTranslation();

    const [dialogInstance, setDialogInstance] = useState();
    const [okButtonDisabled, setOkButtonDisabled] = useState(true);
    const [formData, setFormData] = useState({});
    const [keepOpen, setKeepOpen] = useState(false);

    const [notification, setNotification] = useState({
        open: false,
        severity: "success",
        msg: "",
    });

    const [fakeInputNotInspectedGrid, setFakeInputNotInspectedGrid] = useState();
    const [fakeInputNotInspectedApi, setFakeInputNotInspectedApi] = useState();
    const [fakeInputNotInspectedSelect, setFakeInputNotInspectedSelect] = useState();

    const [fakeInputDetailApi, setFakeInputDetailApi] = useState();

    const authProfile = useRecoilValue(authProfileState);

    if (schema && !schema.columnMap) {
        let columnMap = {};

        for (const column of schema.columns) {
            columnMap[column.name] = column;
        }

        schema.columnMap = columnMap;
    }

    useEffect(() => {
        (async () => {
            if (fakeInputNotInspectedGrid) {
                fakeInputNotInspectedGrid.selectionChanged.addHandler(
                    debounce((grid, event) => {
                        if (grid.selectedItems && grid.selectedItems[0]) {
                            setFakeInputNotInspectedSelect(grid.selectedItems[0]);
                        } else {
                            setFakeInputNotInspectedSelect([]);
                        }
                    }, 10)
                );

                fakeInputNotInspectedGrid.itemsSourceChanged.addHandler(
                    debounce((grid, event) => {
                        if (grid.selectedItems && grid.selectedItems[0]) {
                            setFakeInputNotInspectedSelect(grid.selectedItems[0]);
                        } else {
                            setFakeInputNotInspectedSelect([]);
                        }
                    }, 10)
                );
            }
        })();
    }, [fakeInputNotInspectedGrid]);

    useEffect(() => {
        if (fakeInputNotInspectedSelect) {
            setFormData((formData) => {
                let newFormData = {
                    ...formData,

                    orderPurchaseId: fakeInputNotInspectedSelect.orderPurchaseId,
                    materialFakeInputId: fakeInputNotInspectedSelect.materialFakeInputId,
                    orderPurchaseName: fakeInputNotInspectedSelect.orderPurchaseName,
                    companyName: fakeInputNotInspectedSelect.companyName,
                    dueDate: fakeInputNotInspectedSelect.dueDate,
                    fakeInputDate: fakeInputNotInspectedSelect.fakeInputDate,
                    inspector: authProfile.username,
                    inspectionDate: new Date(),
                };

                for (const column of schema.columns) {
                    if (column?.defaultValue !== undefined) {
                        newFormData[column.name] = column.defaultValue;
                    }
                }

                setOkButtonDisabled(false);

                return newFormData;
            });

            if (fakeInputDetailApi) {
                fakeInputDetailApi.refresh();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schema, fakeInputNotInspectedSelect]);

    const notifySuccess = (msg) => {
        setNotification({
            severity: "success",
            msg: msg,
            open: true,
        });
    };

    const notifyFailure = (msg) => {
        setNotification({
            severity: "error",
            msg: msg,
            open: true,
        });
    };

    const notifyWarning = (msg) => {
        setNotification({
            severity: "warning",
            msg: msg,
            open: true,
        });
    };

    const onNotifySuccess = (msg) => {
        notifySuccess(msg);
    };

    const onDialogInitialized = (instance) => {
        setDialogInstance(instance);

        if (onInitialized) {
            onInitialized(instance);
        }
    };

    const onApiSuccess = (result) => {
        if (fakeInputNotInspectedApi) {
            fakeInputNotInspectedApi.refresh();
        }

        setKeepOpen((keepOpen) => {
            if (!keepOpen) {
                dialogInstance.hide();

                if (onSuccess) {
                    setTimeout(() => onSuccess(result.data), 0);
                }
            } else {
                if (onSuccess) {
                    setTimeout(() => onSuccess(result.data), 0);
                } else {
                    onNotifySuccess(result.data);
                }
            }

            return keepOpen;
        });
    };

    const onApiFailure = (reason) => {
        notifyFailure(
            t(failureKey("insert", reason.response?.status), { reason: reason.response ? reason.response.data : JSON.stringify(reason) })
        );
    };

    const checkItemValidation = (schema, formData) => {
        for (const column of schema.columns) {
            let [valid, msg] = checkValidation("edit", column, column.validation, formData[column.name]);

            if (!valid) {
                notifyWarning(msg);
                return false;
            }
        }

        return true;
    };

    const onOk = (schema, formData) => {
        if (!checkItemValidation(schema, formData)) {
            console.error("Form Validation Failed");

            return;
        }

        if (schema.validation) {
            let [valid, msg] = checkConditionWithMsg(formData, schema.validation);

            if (!valid) {
                notifyWarning(t("warning.formDataValidationFail", { msg: msg }));
                return;
            }
        }

        // build base api url

        let url;

        url = "/api/EguInputInspection";

        let apiData = { ...formData };

        let form = new FormData();

        let json = {};
        let deleted = {};

        for (const formItem in apiData) {
            if (schema.columnMap && schema.columnMap[formItem] && schema.columnMap[formItem].formItemType === "file") {
                deleted[formItem] = apiData[formItem].deletedFiles;

                for (const file of apiData[formItem].activeFiles) {
                    if (file.type === "url") continue;

                    form.append(formItem, file.file);
                }
            } else {
                if (apiData[formItem] !== undefined) {
                    json[formItem] = apiData[formItem];
                }
            }
        }

        form.append("deleted", JSON.stringify(deleted));
        form.append("json", JSON.stringify(json));

        apiClient
            .post(url, form, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            })
            .then((result) => onApiSuccess(result))
            .catch((reason) => onApiFailure(reason));
    };

    const onCancel = () => {
        setFormData(null);

        dialogInstance.hide();
    };

    const checkKeepOpen = () => {
        return (
            <React.Fragment>
                <FormControlLabel
                    control={
                        <Checkbox
                            className={classes.keepOpenSwitch}
                            checked={keepOpen}
                            color="primary"
                            onChange={() => setKeepOpen((keepOpen) => !keepOpen)}
                            name="checkKeepOpen"
                            size="small"
                        />
                    }
                    label={<span className={classes.label}>{t("term.keepDialogOpen")}</span>}
                />
                <div style={{ flexGrow: 1 }}></div>
            </React.Fragment>
        );
    };

    const onChangeFormData = (formData) => {
        setFormData(formData);
        setOkButtonDisabled(false);
    };

    const onCloseNotification = () => {
        setNotification((notification) => {
            return { ...notification, open: false };
        });
    };

    const onCloseDialog = () => {
        setFormData();

        if (onClose) {
            onClose();
        }
    };

    return (
        <Dialog
            id={id}
            moveable={moveable ? moveable : uiConf.dialogMoveable}
            resizable={resizable ? resizable : uiConf.dialogResizable}
            fullWidth={fullWidth ? fullWidth : uiConf.dialogFullWidth}
            maxWidth="lg"
            onClose={onCloseDialog}
            onInitialized={onDialogInitialized}
            {...others}
        >
            <DialogHeaderWithClose>
                <DialogTitle>{t("dialog.title.EguInputInspection.register")}</DialogTitle>
            </DialogHeaderWithClose>

            <Divider />

            <DialogContent style={{ padding: "0px", height: "600px" }}>
                <ReflexContainer orientation="vertical">
                    <ReflexElement flex={0.65}>
                        <ReflexContainer orientation="horizontal">
                            <ReflexElement flex={0.5} data-cy="Master">
                                <BasicCrudScreenType
                                    id="EguVwFakeInputNotInspected"
                                    embededInOtherScreenType
                                    view="EguVwFakeInputNotInspected"
                                    headerTitle={t("term.fakeInput")}
                                    noCreate
                                    noEdit
                                    noDelete
                                    noExcel
                                    onInitialized={(grid) => {
                                        grid.selectionMode = "Row";
                                        setFakeInputNotInspectedGrid(grid);
                                    }}
                                    onApiInitialized={(api) => {
                                        setFakeInputNotInspectedApi(api);
                                    }}
                                />
                            </ReflexElement>

                            <ReflexSplitter style={{ height: layoutConf.reflex.splitterHeight }} />

                            <ReflexElement flex={0.5} data-cy="Detail">
                                {fakeInputNotInspectedSelect?.materialFakeInputId ? (
                                    <BasicCrudScreenType
                                        embededInOtherScreenType
                                        noCreate
                                        noEdit
                                        noDelete
                                        noExcel
                                        id="EguInputInspectionDetail"
                                        view="EguVwMaterialFakeInputDetail"
                                        headerTitle={t("term.fakeInputDetail")}
                                        onModifyViewUrl={(url) =>
                                            `${url}/${fakeInputNotInspectedSelect.materialFakeInputId}`
                                        }
                                        onInitialized={(grid) => {
                                            grid.selectionMode = "None";
                                        }}
                                        onApiInitialized={(api) => {
                                            setFakeInputDetailApi(api);
                                        }}
                                    />
                                ) : (
                                    <React.Fragment></React.Fragment>
                                )}
                            </ReflexElement>
                        </ReflexContainer>
                    </ReflexElement>

                    <ReflexSplitter style={{ width: layoutConf.reflex.splitterWidth }} />

                    <ReflexElement flex={0.35}>
                        <div style={{ padding: "16px" }}>
                            <AutoForm
                                id="egu-input-inspection-register-form"
                                schema={schema}
                                mode="edit"
                                initialData={formData}
                                onChange={onChangeFormData}
                                modifyFormDataCallback={(formData) => {
                                    if (formData.qcResult === defineConf.qcResult.QC_OK) {
                                        formData.defectiveCode = "INS000";
                                    }
                                }}
                            />
                        </div>
                    </ReflexElement>
                </ReflexContainer>

                <Snackbar
                    open={notification.open && notification.severity !== "success"}
                    autoHideDuration={uiConf.dialogNotifyAutoHideDuration}
                    onClose={onCloseNotification}
                >
                    <Alert onClose={onCloseNotification} variant="filled" severity={notification.severity}>
                        {notification.msg}
                    </Alert>
                </Snackbar>
            </DialogContent>

            <Divider />

            <DialogActionsOkCancel
                firstActionComponent={checkKeepOpen}
                //labels={[t("common.add")]}
                buttonDisabled={[okButtonDisabled, false]} /* [okButtonDisabled, cancelButtonDisabled] */
                onOk={() => onOk(schema, formData)}
                onCancel={onCancel}
            />
        </Dialog>
    );
}

export default EguInputInspectionRegisterDialog;
